import api from './axios';
import queryString from 'query-string';
import { VendorAlias, VendorAliasListResponse, UpdateVendorAliasRequest, CreateVendorAliasRequest, StandardAccountListResponse, QboCustomerListResponse, QboVendorListResponse, CreateStandardAccountRequest, UpdateStandardAccountRequest, StandardAccount, BusinessInventoryItemListResponse, InventoryItemAliasListResponse, CreateInventoryItemAliasRequest, UpdateInventoryItemAliasRequest, InventoryItemAlias, NormalizedDescriptionOverride, CreateNormalizedDescriptionOverrideRequest, UpdateNormalizedDescriptionOverrideRequest } from './types';

const normalizeAccountNumber = (raw: string | null | undefined, fallbackId?: string | null): string => {
    const value = (raw || '').trim();
    const idValue = (fallbackId || '').trim();

    if (value) return value;
    if (!idValue) return '';

    // For seeded template ids like 722110_Dine-In_Restaurant_7220, extract the numeric tail.
    if (idValue.includes('_')) {
        const parts = idValue.split('_').filter(Boolean);
        const last = parts[parts.length - 1] || '';
        if (/^\d+$/.test(last)) return last;
    }

    return '';
};

export const vendorApi = {
    getAliases: async (
        resolutionStatus?: string,
        vendorName?: string,
        page: number = 1,
        pageSize: number = 50,
        sortBy?: string,
        sortOrder?: 'asc' | 'desc',
        direction?: string,
        fiscalBucket?: string,
        businessId?: string
    ): Promise<VendorAliasListResponse> => {
        try {
            const params = {
                resolution_status: resolutionStatus,
                vendor_name: vendorName,
                page,
                page_size: pageSize,
                sort_by: sortBy,
                sort_order: sortOrder,
                direction,
                fiscal_bucket: fiscalBucket,
                business_id: businessId
            };
            const response = await api.get('/vendor/aliases', {
                params,
                paramsSerializer: (params) => queryString.stringify(params, { skipNull: true, skipEmptyString: true })
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch vendor aliases:', error);
            throw error;
        }
    },

    getAliasById: async (id: string, business_id?: string): Promise<VendorAlias> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.get(`/vendor/aliases/${id}`, config);
            return response.data;
        } catch (error) {
            console.error(`Failed to fetch vendor alias ${id}:`, error);
            throw error;
        }
    },

    getAliasByLookup: async (rawNormName: string, direction: string, business_id?: string): Promise<VendorAlias> => {
        try {
            const params = {
                raw_norm_name: rawNormName,
                direction: direction,
                ...(business_id && { business_id })
            };
            const response = await api.get('/vendor/aliases/lookup', { params });
            return response.data;
        } catch (error) {
            console.error(`Failed to fetch vendor alias for ${rawNormName}/${direction}:`, error);
            throw error;
        }
    },

    updateAlias: async (id: string, data: UpdateVendorAliasRequest, business_id?: string): Promise<VendorAlias> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.put(`/vendor/aliases/${id}`, data, config);
            return response.data;
        } catch (error) {
            console.error(`Failed to update vendor alias ${id}:`, error);
            throw error;
        }
    },

    createAlias: async (payload: CreateVendorAliasRequest, business_id?: string): Promise<VendorAlias> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post('/vendor/aliases', payload, config);
            return response.data;
        } catch (error) {
            console.error('Failed to create vendor alias:', error);
            throw error;
        }
    },

    getNormalizedDescriptionOverrides: async (business_id?: string, search?: string): Promise<NormalizedDescriptionOverride[]> => {
        try {
            const params = {
                ...(business_id ? { business_id } : {}),
                ...(search ? { search } : {}),
            };
            const response = await api.get('/vendor/normalized-description-overrides', { params });
            return response.data?.items || [];
        } catch (error) {
            console.error('Failed to fetch normalized description overrides:', error);
            throw error;
        }
    },

    createNormalizedDescriptionOverride: async (
        payload: CreateNormalizedDescriptionOverrideRequest,
        business_id?: string
    ): Promise<NormalizedDescriptionOverride> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post('/vendor/normalized-description-overrides', payload, config);
            return response.data;
        } catch (error) {
            console.error('Failed to create normalized description override:', error);
            throw error;
        }
    },

    updateNormalizedDescriptionOverride: async (
        overrideId: string,
        payload: UpdateNormalizedDescriptionOverrideRequest,
        business_id?: string
    ): Promise<NormalizedDescriptionOverride> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.put(`/vendor/normalized-description-overrides/${overrideId}`, payload, config);
            return response.data;
        } catch (error) {
            console.error(`Failed to update normalized description override ${overrideId}:`, error);
            throw error;
        }
    },

    batchUpdateAliases: async (aliasIds: string[], updates: UpdateVendorAliasRequest, business_id?: string): Promise<any> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.put('/vendor/aliases/batch-update', {
                alias_ids: aliasIds,
                updates: updates
            }, config);
            return response.data;
        } catch (error) {
            console.error('Failed to batch update vendor aliases:', error);
            throw error;
        }
    },

    getStandardAccounts: async (businessId?: string): Promise<StandardAccountListResponse> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.get('/vendor/standard-accounts', { params });
            const payload = response.data;
            const items = (payload?.items || []).map((item: any) => ({
                ...item,
                account_number: normalizeAccountNumber(item?.account_number, item?._id),
            }));
            return {
                ...payload,
                items,
            };
        } catch (error) {
            console.error('Failed to fetch standard accounts:', error);
            throw error;
        }
    },

    createStandardAccount: async (data: CreateStandardAccountRequest, businessId?: string): Promise<StandardAccount> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post('/vendor/standard-accounts', data, { params });
            return {
                ...response.data,
                account_number: normalizeAccountNumber(response.data?.account_number, response.data?._id),
            };
        } catch (error) {
            console.error('Failed to create standard account:', error);
            throw error;
        }
    },

    updateStandardAccount: async (accountId: string, data: UpdateStandardAccountRequest, businessId?: string): Promise<StandardAccount> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.put(`/vendor/standard-accounts/${accountId}`, data, { params });
            return {
                ...response.data,
                account_number: normalizeAccountNumber(response.data?.account_number, response.data?._id),
            };
        } catch (error) {
            console.error(`Failed to update standard account ${accountId}:`, error);
            throw error;
        }
    },

    deleteStandardAccount: async (accountId: string, businessId?: string): Promise<{ deleted: boolean; account_id: string }> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.delete(`/vendor/standard-accounts/${accountId}`, { params });
            return response.data;
        } catch (error) {
            console.error(`Failed to delete standard account ${accountId}:`, error);
            throw error;
        }
    },

    renormalizeVendors: async (params: {
        batchSize?: number;
        dryRun?: boolean;
        forceReprocess?: boolean;
        resetQboData?: boolean;
        onlyChecks?: boolean;
        excludeChecks?: boolean;
        onlyNeedsReview?: boolean;
        businessId?: string;
    } = {}): Promise<any> => {
        try {
            const queryParams = {
                batch_size: params.batchSize ?? 100,
                dry_run: params.dryRun ?? false,
                force_reprocess: params.forceReprocess ?? true,
                reset_qbo_data: params.resetQboData ?? true,
                only_checks: params.onlyChecks ?? false,
                exclude_checks: params.excludeChecks ?? false,
                only_needs_review: params.onlyNeedsReview ?? false,
                ...(params.businessId && { business_id: params.businessId })
            };
            const response = await api.post('/vendor-analytics/renormalize-vendors', null, {
                params: queryParams
            });
            return response.data;
        } catch (error) {
            console.error('Failed to trigger renovation:', error);
            throw error;
        }
    },

    resolvePendingVendors: async (params: {
        batchSize?: number;
        excludeChecks?: boolean;
        businessId?: string;
    } = {}): Promise<{ status: string; message: string; details?: string }> => {
        try {
            const queryParams = {
                batch_size: params.batchSize ?? 10,
                exclude_checks: params.excludeChecks ?? false,
                ...(params.businessId && { business_id: params.businessId }),
            };
            const response = await api.post('/vendor-analytics/resolve-pending-vendors', null, {
                params: queryParams,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to trigger vendor resolution:', error);
            throw error;
        }
    },

    getIndustries: async (): Promise<import('./types').Industry[]> => {
        try {
            const response = await api.get('/vendor/industries');
            return response.data;
        } catch (error) {
            console.error('Failed to fetch industries:', error);
            throw error;
        }
    },

    getQBOAccounts: async (businessId?: string): Promise<import('./types').QBOAccountListResponse> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.get('/vendor/qbo-accounts', { params });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch QBO accounts:', error);
            throw error;
        }
    },

    syncQboAccountMappings: async (
        businessId?: string,
        options?: { forceRetry?: boolean }
    ): Promise<import('./types').QboAccountMappingSyncResponse> => {
        try {
            const params = {
                ...(businessId ? { business_id: businessId } : {}),
                ...(options?.forceRetry ? { force_retry: true } : {}),
            };
            const response = await api.post('/vendor/qbo-account-mappings/sync', null, { params });
            return response.data;
        } catch (error) {
            console.error('Failed to sync QBO account mappings:', error);
            throw error;
        }
    },

    updateQboAccountMapping: async (
        qboAccountId: string,
        payload: import('./types').UpdateQboAccountMappingRequest,
        businessId?: string
    ): Promise<import('./types').UpdateQboAccountMappingResponse> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.put(`/vendor/qbo-account-mappings/${qboAccountId}`, payload, { params });
            return response.data;
        } catch (error) {
            console.error(`Failed to update QBO account mapping ${qboAccountId}:`, error);
            throw error;
        }
    },

    reapplyQboAccountMappings: async (
        payload: { qbo_account_id?: string },
        businessId?: string
    ): Promise<import('./types').QboAccountMappingReapplyResponse> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post('/vendor/qbo-account-mappings/reapply', payload, { params });
            return response.data;
        } catch (error) {
            console.error('Failed to reapply QBO account mappings:', error);
            throw error;
        }
    },

    getQboVendorsList: async (
        displayName?: string,
        page: number = 1,
        pageSize: number = 100,
        businessId?: string
    ): Promise<QboVendorListResponse> => {
        try {
            const params = {
                display_name: displayName,
                page,
                page_size: pageSize,
                business_id: businessId,
            };
            const response = await api.get('/vendor/list', {
                params,
                paramsSerializer: (queryParams) => queryString.stringify(queryParams, { skipNull: true, skipEmptyString: true }),
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch QBO vendors list:', error);
            throw error;
        }
    },
    getQboCustomersList: async (
        displayName?: string,
        page: number = 1,
        pageSize: number = 100,
        businessId?: string
    ): Promise<QboCustomerListResponse> => {
        try {
            const params = {
                display_name: displayName,
                page,
                page_size: pageSize,
                business_id: businessId,
            };
            const response = await api.get('/vendor/customers/list', {
                params,
                paramsSerializer: (queryParams) => queryString.stringify(queryParams, { skipNull: true, skipEmptyString: true }),
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch QBO customers list:', error);
            throw error;
        }
    },
    getInventoryItems: async (
        search?: string,
        page: number = 1,
        pageSize: number = 100,
        businessId?: string,
    ): Promise<BusinessInventoryItemListResponse> => {
        try {
            const params = {
                search,
                page,
                page_size: pageSize,
                business_id: businessId,
            };
            const response = await api.get('/vendor/inventory-items', {
                params,
                paramsSerializer: (queryParams) => queryString.stringify(queryParams, { skipNull: true, skipEmptyString: true }),
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch business inventory items:', error);
            throw error;
        }
    },
    getItemAliases: async (
        params: {
            search?: string;
            resolutionStatus?: string;
            vendorName?: string;
            page?: number;
            pageSize?: number;
            businessId?: string;
        } = {}
    ): Promise<InventoryItemAliasListResponse> => {
        try {
            const queryParams = {
                search: params.search,
                resolution_status: params.resolutionStatus,
                vendor_name: params.vendorName,
                page: params.page ?? 1,
                page_size: params.pageSize ?? 50,
                business_id: params.businessId,
            };
            const response = await api.get('/vendor/item-aliases', {
                params: queryParams,
                paramsSerializer: (queryParams) => queryString.stringify(queryParams, { skipNull: true, skipEmptyString: true }),
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch inventory item aliases:', error);
            throw error;
        }
    },
    createItemAlias: async (
        payload: CreateInventoryItemAliasRequest,
        businessId?: string,
    ): Promise<InventoryItemAlias> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post('/vendor/item-aliases', payload, { params });
            return response.data;
        } catch (error) {
            console.error('Failed to create inventory item alias:', error);
            throw error;
        }
    },
    updateItemAlias: async (
        aliasId: string,
        payload: UpdateInventoryItemAliasRequest,
        businessId?: string,
    ): Promise<InventoryItemAlias> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.put(`/vendor/item-aliases/${aliasId}`, payload, { params });
            return response.data;
        } catch (error) {
            console.error(`Failed to update inventory item alias ${aliasId}:`, error);
            throw error;
        }
    },
    getTopExpenses: async (businessId?: string, startDate?: string, endDate?: string): Promise<import('./types').TopExpensesResponse> => {
        try {
            const params = {
                ...(businessId && { business_id: businessId }),
                ...(startDate && { start_date: startDate }),
                ...(endDate && { end_date: endDate })
            };
            const response = await api.get('/vendor-analytics/top-expenses', { params });
            // API returns { success: true, data: { last_30d: [...], last_60d: [...], last_90d: [...] } }
            const d = response.data;
            const periodData = d.data || d;
            return {
                last_30d: periodData.last_30d || periodData.top_expenses || (Array.isArray(periodData) ? periodData : []),
                last_60d: periodData.last_60d || [],
                last_90d: periodData.last_90d || [],
            };
        } catch (error) {
            console.error('Failed to fetch top expenses:', error);
            throw error;
        }
    }
};

export default vendorApi;
